import { Logger, debounce } from "$lib/core";
import {
  FormRestructureEvent,
  getFieldFillableElements,
} from "../core";

const predicateLogger = new Logger("FormsDependsOnPredicate");
const evalLogger = new Logger("FormsDependsOnEval");

Logger.addLogger("FormsDependsOn");

/**
 * Monitors elements with an attribute of `data-depends-on="<predicate>"` in a given form and updates visibility of those elements based on the truthy-ness of the parsed expression.
 * The expression's variables are assumed to be the names of input/select/textarea elements elsewhere in the form.
 *
 * @param {HTMLFormElement} form
 */
export function addDependsOn(form) {
  const dependsAttributeName = "data-depends-on";

  const debounceTime = 200;

  /** @type {Map<HTMLElement, Function[]>} Tracks event listener callbacks watching for changes on each respective depended-on element (the actual inputs/selects/textareas). */
  const handlerMap = new Map();
  /** @type {Set<HTMLElement>} The fields and fieldgroups that should be conditionally displayed & which have the `[data-depends-on]` attribute. */
  const watchedElements = new Set();
  /** @type {WeakMap<HTMLElement, ((values: Record<string, any>) => boolean)>} Caches the parsed & constructed predicate for each element. */
  const predicateMap = new WeakMap();
  /** @type {Map<HTMLElement, Set<string>>} */
  const dependentNamesMap = new Map();
  /** @type {RegExp} Matches an identifier (possibly `.`-delimited) that is either beside boolean operators/parentheses or standalone. */
  const variableRegex = /(?<=^|\s|[=!<>|&()])([a-zA-Z_][a-zA-Z0-9_.]*)(?=\s*([=!<>|&()]|$))|^([a-zA-Z_][a-zA-Z0-9_.]*)$/g;

  for (const node of form.querySelectorAll(
    `:scope [${dependsAttributeName}]`
  )) {
    if (node instanceof HTMLElement) {
      watchedElements.add(node);
    }
  }

  /**
   * Evaluates the boolean predicate from `data-depends-on`.
   * @param {string} expression The boolean expression referencing form names.
   * @param {HTMLElement} element The element with the `data-depends-on` attribute whose attribute we are parsing.
   * @returns {((values: Record<string, any>) => boolean)} The result of the evaluated expression.
   */
  function compilePredicate(expression, element) {
		if (!expression) return () => false;
    try {
      // Replace form item names with actual values in the expression
      const safeExpression = expression.replace(
        variableRegex,
        (match) => match === "true" || match === "false" ? match : `(values[${JSON.stringify(match)}] ?? "")`
      );
      const func = Function(
        "values",
        `"use strict"; return (${safeExpression});`
      );
      predicateLogger.debug("Compiling 'data-depends-on' predicate", {
        element,
        safeExpression,
        func,
      });
      return /** @type {((values: Record<string, any>) => boolean)} */ (func);
    } catch (error) {
      console.error(
        `Invalid expression in ${dependsAttributeName}:`,
        expression,
        error
      );
      return () => false;
    }
  }

  /**
   * Re-evaluates all watched elements' dependencies.
   */
  function updateVisibility() {
    const elements =
      /** @type {import("$lib/core/js/util/defs").FormControl[]} */ (
        Array.from(form.elements)
      );
    const values = elements
      .filter((el) => el.name && !el.disabled)
      .reduce((obj, next) => {

        if (!next.name) return obj;

        if (next.tagName === "INPUT") {
          const elementsWithThatName = form.elements.namedItem(next.name);
          // This field appears multiple times in the collection, i.e. "checkboxes", "radios", C# "checkbox" TagHelper...
          if (elementsWithThatName instanceof RadioNodeList) {
            const dupElements = /** @type {import("$lib/core/js/util/defs").FormControl[]} */ ([...elementsWithThatName]);

            // Omit C# Razor TagHelpers' extra generated "hidden" input for `<input type="checkbox" asp-for="..." />` elements.
            if (next.type === "hidden" && dupElements.some(el => el.type === "checkbox" && el.dataset.val === "true")) {
              evalLogger.debug("Skipping element: ", next);
              return obj;
            }
          }
        }

        const value = next.type === "checkbox" ? next.checked : (next.value === "false" ? false : next.value);
        evalLogger.debug("Adding value: ", { name: next.name, type: next.type, checked: next.checked, value: next.value, disabled: next.disabled, actual: value });
        obj[next.name] = value;
        return obj;
      }, /** @type {Record<string, any>} */ ({}));

    watchedElements.forEach((element) => {
      let predicate = predicateMap.get(element);
      if (!predicate) {
        const expression = element.dataset.dependsOn ?? "false";
        predicate = compilePredicate(expression, element);
        predicateMap.set(element, predicate);
      }

      const shouldBeHidden = !predicate(values);
      const field = /** @type {HTMLElement} */ (element.closest(".field,.fieldgroup"));
      const visibilityElement = field ?? element;
      evalLogger.debug(`Element depends on evaluation:`, {
        field: visibilityElement,
        element,
        values,
        predicate,
        shouldBeHidden,
      });
      updateElementVisibility(element, visibilityElement, shouldBeHidden);
    });
  }

  /**
   * @param {HTMLElement} element
   * @param {HTMLElement} visibilityElement
   * @param {boolean} shouldBeHidden
   */
  function updateElementVisibility(element, visibilityElement, shouldBeHidden) {
    if (visibilityElement.dataset.dependsOn && visibilityElement.dataset.dependsShow !== "true") {
      visibilityElement.classList.toggle("display", !shouldBeHidden);
    }

    // If this is a literal fieldset rather than a "field" that encompasses multiple form controls (such as "checkboxes" fields)...
    if (element instanceof HTMLFieldSetElement && !element.classList.contains("fieldgroup")) {
      for (const control of element.elements) {
        updateElementVisibility(/** @type {HTMLElement} */ (control), visibilityElement, shouldBeHidden);
      }
      return;
    }

    /**
     * @param {HTMLElement} element
     * @param {HTMLElement} visibilityElement
     * @param {boolean} shouldBeHidden 
     */
    function updateElement(element, visibilityElement, shouldBeHidden) {
      const el = /** @type {import("$lib/core/js/util/defs").FormControl} */ (element);
      let isReadOnly = visibilityElement.dataset.dependsReadonly === "true" || el.dataset.dependsReadonly === "true";
      if (isReadOnly && "readonly" in el) {
        el.readonly = shouldBeHidden;
        if (shouldBeHidden) {
          el.setAttribute("readonly", "true");
          el.setAttribute("aria-readonly", "true");
        } else {
          el.removeAttribute("readonly");
          el.removeAttribute("aria-readonly");
        }
      } else {
        el.disabled = shouldBeHidden;
        if (shouldBeHidden) {
          el.setAttribute("aria-disabled", "true");
        } else {
          el.removeAttribute("aria-disabled");
        }
      }
    }

    if ("disabled" in element) {
      updateElement(element, visibilityElement, shouldBeHidden);
    } else {
      for (const fillable of getFieldFillableElements(visibilityElement)) {
        updateElement(fillable, visibilityElement, shouldBeHidden);
      }
    }
  }

  /**
   * Event listener with debounce support for `<input>` and `<textarea>`.
   * @param {Event} event - The event triggering the re-evaluation.
   */
  function handleEvent(event) {
    const element = /** @type {HTMLElement} */ (event.target);
    if (
      event.type === "input" &&
      (element.tagName === "INPUT" || element.tagName === "TEXTAREA")
    ) {
      debounce(updateVisibility, debounceTime, { key: event.target });
    } else {
      updateVisibility();
    }
  }

  /**
   * Update `watchedElements` if the attribute is added or removed from an element.
   * If the attribute is merely modified, re-compile the predicate.
   *
   * @param {MutationRecord[]} entries
   */
  function handleAttributeChange(entries) {
    for (const entry of entries) {
      const element = /** @type {HTMLElement} */ (entry.target);
      updateCache(element, !element.dataset.dependsOn);
    }
    updateVisibility();
  }

  /**
   * @param {HTMLElement} element
   * @param {boolean} [remove=false]
   */
  function updateCache(element, remove = false) {
    if (!remove) {
      if (!watchedElements.has(element)) {
        watchedElements.add(element);
      }
      const expression = element.dataset.dependsOn ?? "false";
      predicateMap.set(element, compilePredicate(expression, element));

      // Clear dependencies before we regenerate them.
      let dependencies = dependentNamesMap.get(element);
      if (dependencies === undefined) {
        dependencies = new Set();
        dependentNamesMap.set(element, dependencies);
      } else {
        dependencies.clear();
      }
      // Extract variable names
      expression.match(variableRegex)?.forEach((name) => {
        if (!dependencies.has(name)) {
          dependencies.add(name);
        }
      });
    } else {
      watchedElements.delete(element);
      predicateMap.delete(element);
      dependentNamesMap.delete(element);
    }
  }

  /**
	 * Returns true if any field/fieldgroup element has declared a dependency on a name matching `element.name`.
   * @param {HTMLElement & { name: string }} element
   */
  function isDependedOn(element) {
    return [...dependentNamesMap.values()].some((set) =>
      /** @type {Set<string>} */ (set).has(element.name)
    );
  }

  /**
   * @param {HTMLElement & { name: string }} element The form item to which listeners are attached/removed.
	 * @param {boolean} [remove=false]
   */
  function updateDependencyListeners(element, remove = false) {
    if (element instanceof HTMLSelectElement) {
      if (isDependedOn(element)) {
				if (remove) {
					element.removeEventListener("change", handleEvent);
				} else {
					element.addEventListener("change", handleEvent);
				}
      }
    } else if (
      element instanceof HTMLInputElement ||
      element instanceof HTMLTextAreaElement
    ) {
      //const el = (element instanceof HTMLInputElement && element.dataset.val === "true" && element.form != null)
      //  ? /** @type {HTMLInputElement} */ (element.form.querySelector(`:scope input[name='${element.name}'][type='hidden']`))
      //  : element;
      if (isDependedOn(element)) {
				if (remove) {
					element.removeEventListener("change", handleEvent);
					element.removeEventListener("input", handleEvent);
				} else {
					element.addEventListener("change", handleEvent);
					element.addEventListener("input", handleEvent);
				}
      }
    }
  }

	/**
	 * @param {HTMLElement} element The field/fieldgroup that needs dependencies updated.
	 */
	function updateFieldDependencies(element) {
		const dependencies = dependentNamesMap.get(element);
		if (dependencies) {
			for (const name of dependencies) {
				const result = form.elements.namedItem(name);
				if (result == null) continue;
				if (result instanceof Element) {
					updateDependencyListeners(/** @type {HTMLElement & { name: string }} */ (result));
				} else {
					for (const node of result) {
						updateDependencyListeners(/** @type {HTMLElement & { name: string }} */ (node));
					}
				}
			}
		}
	}

  // Initialize caches for watched elements
  watchedElements.forEach((element) => {
    updateCache(element);
  });

  const observer = new MutationObserver(handleAttributeChange);
  //watchedElements.forEach(element => observer.observe(element, { attributeFilter: [dependsAttributeName], attributeOldValue: true }));

  form.addEventListener("restructure", async (event) => {
    if (!(event instanceof FormRestructureEvent)) return;

    for (const entry of event.added) {
      if (!entry.field) continue;

      const watchExpression = entry.field.dataset.dependsOn;
      if (!watchExpression) continue;

      watchedElements.add(entry.field);
      observer.observe(entry.field, {
        attributeFilter: [dependsAttributeName],
        attributeOldValue: true,
      });
      updateCache(entry.field);
			updateFieldDependencies(entry.field);
    }

    for (const entry of event.removed) {
      if (!entry.field) continue;
      if (!(entry.field instanceof HTMLElement)) continue;
      watchedElements.delete(entry.field);
			updateFieldDependencies(entry.field);
    }

    if (event.init) {
      updateVisibility();
    }
  });

  Array.from(form.elements).forEach((e) => {
    const element = /** @type {HTMLElement & { name: string }} */ (e);
    updateDependencyListeners(element);
  });

  // Re-evaluate initial state based on parsed expressions.
  updateVisibility();
}
